import { Injectable } from '@angular/core';
import { StorageService } from './storage.service';

/**
 * Provides a storage service that uses cookies to store JSON
 */
@Injectable({ providedIn: 'root' })
export class CookieStorageService extends StorageService {
  /**
   * Gets the cookie with the provided key
   * @param key The key to retrieve
   */
  public override read(key: string): any {
    const cookies = this.getCookies();
    const generatedKey = this.getKey(key);

    if (cookies[generatedKey]) {
      return JSON.parse(cookies[generatedKey]);
    }
  }

  /**
   * Writes the cookie with the provided key and value
   * @param key The cookie name to set
   * @param value The value of the cookie
   */
  public override write(key: string, value: any): void {
    const cookie =
      encodeURIComponent(this.getKey(key)) +
      '=' +
      encodeURIComponent(JSON.stringify(value));

    if (window.location.protocol === 'http:') {
      // Otherwise we do not include the secure flag as it will prevent the cookie
      // from being created
      document.cookie = cookie + ';SameSite=Strict;path=/;';
    } else {
      // If we are running over a secure connection then we will use a secure cookie
      document.cookie = cookie + ';secure;SameSite=Strict;path=/;';
    }
  }

  /**
   * Deletes the cookie with the provided key
   * @param key They cookie name to delete
   */
  public override remove(key: string): void {
    document.cookie =
      encodeURIComponent(this.getKey(key)) +
      '=;expires=Thu, 01 Jan 1970 00:00:01 GMT;';
  }

  /**
   * Deletes all the keys from the storage service created by csx-security (i.e. keys with clientId as prefix)
   */
  public override clear(): void {
    Object.keys(this.getCookies())
      .filter(key => key?.endsWith(this.postfix))
      .forEach(key => {
        this.remove(key);
      });
  }

  /**
   * Returns a dictionary with the cookie values that are stored
   */
  private getCookies(): { [key: string]: string } {
    const cookieDough = document.cookie;
    const cookieJar: { [key: string]: string } = {};
    let cookies;

    if (cookieDough && cookieDough.length > 0) {
      cookies = cookieDough.split(';');
      for (let cookie of cookies) {
        let cookieParts = cookie.split('=');

        if (cookieParts && cookieParts.length === 2) {
          //Fixes EC-4926
          let key: string = cookieParts[0].trim();
          let value: string = cookieParts[1].trim();

          cookieJar[decodeURIComponent(key)] = decodeURIComponent(value);
        }
      }
    }

    return cookieJar;
  }
}
